import { useCallback, useEffect, useState } from 'react';
import { CustomerCategory } from '../types';
import { customerCategoriesService } from '../services/customerCategories';

type CommercialTermsForm = {
  name: string;
  description: string;
  defaultCharterDiscount: number;
  defaultScheduleDiscount: number;
  defaultCreditLimit: number;
};

const EMPTY_FORM: CommercialTermsForm = {
  name: '',
  description: '',
  defaultCharterDiscount: 0,
  defaultScheduleDiscount: 0,
  defaultCreditLimit: 0,
};

export const useCommercialTerms = () => {
  const [categories, setCategories] = useState<CustomerCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CustomerCategory | null>(null);
  const [formData, setFormData] = useState<CommercialTermsForm>({ ...EMPTY_FORM });

  const loadCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await customerCategoriesService.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openModal = (category?: CustomerCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        defaultCharterDiscount: category.defaultCharterDiscount,
        defaultScheduleDiscount: category.defaultScheduleDiscount,
        defaultCreditLimit: category.defaultCreditLimit,
      });
    } else {
      setEditingCategory(null);
      setFormData({ ...EMPTY_FORM });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ ...EMPTY_FORM });
  };

  const saveCategory = async () => {
    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        await customerCategoriesService.update(editingCategory.id, formData);
      } else {
        await customerCategoriesService.create(formData);
      }
      await loadCategories();
      closeModal();
    } catch (error: any) {
      console.error('Failed to save category:', error);
      alert(error.response?.data?.error || 'Failed to save category');
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category? Customers assigned to it will not be affected, but the category will be deactivated.')) {
      return;
    }
    try {
      await customerCategoriesService.delete(id);
      await loadCategories();
    } catch (error: any) {
      console.error('Failed to delete category:', error);
      alert(error.response?.data?.error || 'Failed to delete category');
    }
  };

  return {
    categories,
    isLoading,
    showModal,
    editingCategory,
    formData,
    setFormData,
    openModal,
    closeModal,
    saveCategory,
    deleteCategory,
  };
};
